/**
 * 数据存储服务
 * 支持本地存储(IndexedDB)和云端同步
 */

import Dexie from 'dexie'

const DB_NAME = 'nav-portal-db'
const DB_VERSION = 1

// Vue 的响应式对象是 Proxy，IndexedDB 的结构化克隆无法序列化，
// 写入前统一转成普通对象，避免 DataCloneError
function toPlain(value) {
  if (value === null || typeof value !== 'object') return value
  return JSON.parse(JSON.stringify(value))
}

class StorageService {
  constructor() {
    this.db = new Dexie(DB_NAME)
    this.initDatabase()
    this.cloudConfig = null
  }

  initDatabase() {
    this.db.version(DB_VERSION).stores({
      categories: '++id, name, priority, &slug',
      bookmarks: '++id, name, url, categoryId, createdAt, visitCount',
      collections: '++id, url, type, platform, createdAt, isFavorite, isRead',
      searchHistory: '++id, keyword, timestamp',
      settings: 'key, value',
      syncMeta: 'key, value'
    })
  }

  // ========== 收藏内容操作 ==========
  async getCollections(filter = {}) {
    let query = this.db.collections.orderBy('createdAt').reverse()
    
    if (filter.type) {
      query = this.db.collections.where('type').equals(filter.type)
    }
    
    return await query.toArray()
  }

  async addCollection(item) {
    return await this.db.collections.add({
      ...toPlain(item),
      createdAt: item.createdAt || Date.now()
    })
  }

  async updateCollection(id, updates) {
    return await this.db.collections.update(id, toPlain(updates))
  }

  async deleteCollection(id) {
    return await this.db.collections.delete(id)
  }

  async searchCollections(keyword) {
    const lowerKeyword = keyword.toLowerCase()
    return await this.db.collections
      .filter(item => 
        item.title?.toLowerCase().includes(lowerKeyword) ||
        item.description?.toLowerCase().includes(lowerKeyword) ||
        item.url?.toLowerCase().includes(lowerKeyword) ||
        (item.tags && item.tags.some(t => t.toLowerCase().includes(lowerKeyword)))
      )
      .toArray()
  }

  // ========== 分类操作 ==========
  async getCategories() {
    return await this.db.categories.orderBy('priority').toArray()
  }

  async addCategory(category) {
    const id = await this.db.categories.add({
      ...toPlain(category),
      createdAt: Date.now()
    })
    return id
  }

  async updateCategory(id, updates) {
    return await this.db.categories.update(id, toPlain(updates))
  }

  async deleteCategory(id) {
    await this.db.bookmarks.where('categoryId').equals(id).delete()
    return await this.db.categories.delete(id)
  }

  // ========== 书签操作 ==========
  async getBookmarks(categoryId = null) {
    if (categoryId) {
      return await this.db.bookmarks.where('categoryId').equals(categoryId).toArray()
    }
    return await this.db.bookmarks.toArray()
  }

  async addBookmark(bookmark) {
    const id = await this.db.bookmarks.add({
      ...toPlain(bookmark),
      createdAt: Date.now(),
      visitCount: 0
    })
    return id
  }

  async updateBookmark(id, updates) {
    return await this.db.bookmarks.update(id, toPlain(updates))
  }

  async deleteBookmark(id) {
    return await this.db.bookmarks.delete(id)
  }

  async incrementVisit(id) {
    const bookmark = await this.db.bookmarks.get(id)
    if (bookmark) {
      await this.db.bookmarks.update(id, {
        visitCount: (bookmark.visitCount || 0) + 1,
        lastVisit: Date.now()
      })
    }
  }

  async searchBookmarks(keyword) {
    const lowerKeyword = keyword.toLowerCase()
    return await this.db.bookmarks
      .filter(b => 
        b.name.toLowerCase().includes(lowerKeyword) ||
        b.url.toLowerCase().includes(lowerKeyword) ||
        (b.description && b.description.toLowerCase().includes(lowerKeyword)) ||
        (b.tags && b.tags.some(t => t.toLowerCase().includes(lowerKeyword)))
      )
      .toArray()
  }

  async getRecentBookmarks(limit = 10) {
    return await this.db.bookmarks
      .orderBy('lastVisit')
      .reverse()
      .limit(limit)
      .toArray()
  }

  async getFrequentBookmarks(limit = 10) {
    return await this.db.bookmarks
      .orderBy('visitCount')
      .reverse()
      .limit(limit)
      .toArray()
  }

  // ========== 搜索历史 ==========
  async addSearchHistory(keyword) {
    const existing = await this.db.searchHistory.where('keyword').equals(keyword).first()
    if (existing) {
      await this.db.searchHistory.update(existing.id, { timestamp: Date.now() })
    } else {
      await this.db.searchHistory.add({
        keyword,
        timestamp: Date.now()
      })
    }
  }

  async getSearchHistory(limit = 20) {
    return await this.db.searchHistory
      .orderBy('timestamp')
      .reverse()
      .limit(limit)
      .toArray()
  }

  async clearSearchHistory() {
    return await this.db.searchHistory.clear()
  }

  // ========== 设置操作 ==========
  async getSetting(key, defaultValue = null) {
    const setting = await this.db.settings.get(key)
    return setting ? setting.value : defaultValue
  }

  async setSetting(key, value) {
    return await this.db.settings.put({ key, value: toPlain(value) })
  }

  // ========== 数据导入导出 ==========
  async exportData() {
    const categories = await this.getCategories()
    const bookmarks = await this.getBookmarks()
    const settings = await this.db.settings.toArray()
    
    return {
      version: DB_VERSION,
      exportTime: Date.now(),
      categories,
      bookmarks,
      settings
    }
  }

  async importData(data) {
    if (!data.categories || !data.bookmarks) {
      throw new Error('无效的导入数据格式')
    }

    await this.db.transaction('rw', this.db.categories, this.db.bookmarks, this.db.settings, async () => {
      await this.db.categories.clear()
      await this.db.bookmarks.clear()
      
      for (const category of data.categories) {
        await this.db.categories.add(category)
      }
      
      for (const bookmark of data.bookmarks) {
        await this.db.bookmarks.add(bookmark)
      }
      
      if (data.settings) {
        for (const setting of data.settings) {
          await this.db.settings.put(setting)
        }
      }
    })
  }

  // ========== 云端同步 ==========
  async configureCloud(config) {
    this.cloudConfig = config
    await this.setSetting('cloudConfig', config)
  }

  async syncToCloud() {
    if (!this.cloudConfig) {
      throw new Error('云端配置未设置')
    }

    const data = await this.exportData()
    
    try {
      const response = await fetch(this.cloudConfig.uploadUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.cloudConfig.token}`
        },
        body: JSON.stringify(data)
      })

      if (!response.ok) {
        throw new Error('云端同步失败')
      }

      await this.db.syncMeta.put({
        key: 'lastSync',
        value: Date.now()
      })

      return { success: true, timestamp: Date.now() }
    } catch (error) {
      console.error('云端同步错误:', error)
      throw error
    }
  }

  async syncFromCloud() {
    if (!this.cloudConfig) {
      throw new Error('云端配置未设置')
    }

    try {
      const response = await fetch(this.cloudConfig.downloadUrl, {
        headers: {
          'Authorization': `Bearer ${this.cloudConfig.token}`
        }
      })

      if (!response.ok) {
        throw new Error('云端获取数据失败')
      }

      const data = await response.json()
      await this.importData(data)

      return { success: true, timestamp: Date.now() }
    } catch (error) {
      console.error('云端同步错误:', error)
      throw error
    }
  }

  async getLastSyncTime() {
    const meta = await this.db.syncMeta.get('lastSync')
    return meta ? meta.value : null
  }

  // ========== 初始化默认数据 ==========
  async initDefaultData(categories, bookmarks) {
    const existingCategories = await this.getCategories()
    
    if (existingCategories.length === 0) {
      for (const category of categories) {
        await this.db.categories.add(category)
      }
      
      for (const bookmark of bookmarks) {
        await this.db.bookmarks.add({
          ...bookmark,
          createdAt: Date.now(),
          visitCount: 0
        })
      }
    }
  }
}

export const storage = new StorageService()
export default storage
